import { useQuery } from '@tanstack/react-query'
import { useEffect } from 'react'
import { Navigate, Outlet, useLocation } from 'react-router'
import { FullPageLoader } from '@/components/feedback/States'
import { UnknownSitePage } from '@/pages/site/UnknownSitePage'
import { WebsitePage } from '@/pages/site/WebsitePage'
import { useAuth } from '@/features/auth/useAuth'
import { flushVocabularyChange, setVocabulary } from '@/i18n'
import { DEFAULT_VOCABULARY } from '@/i18n/vocabulary'
import { siteContextQuery } from './context'

/**
 * Chooses what this address shows: the SchoolEduca website (base domain, www.), the operator
 * console (platform.), one school (its subdomain), or everything (single-address mode, e.g. dev).
 */
export function HostGate() {
  const site = useQuery(siteContextQuery())
  const { pathname, search } = useLocation()
  const isPlatformPath = pathname.startsWith('/platform')
  const leaveForPlatform = site.data?.kind === 'school' && isPlatformPath
  const { school } = useAuth()

  // Texts follow the school's vocabulary (signed-in school, else the school of this address).
  const vocabulary = (isPlatformPath ? null : (school ?? site.data?.school)?.vocabulary) ?? DEFAULT_VOCABULARY
  const vocabularyKey = JSON.stringify(vocabulary)
  // Applied while rendering, so everything rendered below already reads the school's words; the
  // components mounted earlier are told afterwards, once they all listen (effects run bottom-up).
  setVocabulary(vocabulary, false)
  useEffect(flushVocabularyChange, [vocabularyKey])

  useEffect(() => {
    if (leaveForPlatform && site.data) window.location.replace(site.data.platformUrl + pathname + search)
  }, [leaveForPlatform, site.data, pathname, search])

  // If the context cannot be loaded, fall back to the single-address app rather than a blank page.
  if (site.isPending) return <FullPageLoader />
  const kind = site.data?.kind ?? 'any'
  if (kind === 'website') return <WebsitePage context={site.data ?? null} />
  if (kind === 'unknown') return <UnknownSitePage context={site.data ?? null} />
  if (kind === 'platform' && !isPlatformPath) return <Navigate to="/platform" replace />
  if (leaveForPlatform) return <FullPageLoader />
  return <Outlet />
}
